// Operation figures per month and overall: they label the monthly folders of
// the Operaciones and Avisos tabs and fill the PDF summaries, so the screen and
// the downloaded report always show the same numbers.
import { PAYMENT_METHODS } from "./constants";
import { monthOf } from "./dates";
import { roundCents } from "./money";
import type { NoticeObligation } from "./notices";
import { exceedsCashLimit, operationLevel, type Thresholds } from "./thresholds";
import type { IsoDate, IsoMonth, Operation, PaymentMethod } from "./types";

export interface CountAndAmount {
  count: number;
  /** Without VAT. */
  amount: number;
}

export interface NoticeCounts {
  total: number;
  individual: number;
  accumulated: number;
  filed: number;
  /** Not filed yet (overdue ones included). */
  pending: number;
  /** Not filed and past the deadline. */
  overdue: number;
}

export interface OperationsSummary {
  operations: number;
  /** Total without VAT. */
  amount: number;
  byPaymentMethod: Record<PaymentMethod, CountAndAmount>;
  /** Operations at or above the identification threshold (their client's dossier is mandatory). */
  identificationRequired: number;
  /** Cash operations at or above the art. 32 limit (with VAT). */
  cashOverLimit: number;
  /** Notices whose reporting month falls in the period. */
  notices: NoticeCounts;
}

type SummaryOperation = Pick<Operation, "date" | "amount" | "paymentMethod">;
type SummaryNotice = Pick<NoticeObligation, "kind" | "filed" | "deadline" | "month">;

const emptyNoticeCounts = (): NoticeCounts => ({
  total: 0,
  individual: 0,
  accumulated: 0,
  filed: 0,
  pending: 0,
  overdue: 0,
});

function emptySummary(): OperationsSummary {
  return {
    operations: 0,
    amount: 0,
    byPaymentMethod: Object.fromEntries(PAYMENT_METHODS.map((m) => [m, { count: 0, amount: 0 }])) as Record<
      PaymentMethod,
      CountAndAmount
    >,
    identificationRequired: 0,
    cashOverLimit: 0,
    notices: emptyNoticeCounts(),
  };
}

function addOperation(summary: OperationsSummary, op: SummaryOperation, thresholds: Thresholds) {
  summary.operations++;
  summary.amount = roundCents(summary.amount + op.amount);
  const method = summary.byPaymentMethod[op.paymentMethod];
  method.count++;
  method.amount = roundCents(method.amount + op.amount);
  if (operationLevel(op.amount, thresholds) !== "below_threshold") summary.identificationRequired++;
  if (exceedsCashLimit(op, thresholds)) summary.cashOverLimit++;
}

function addNotice(counts: NoticeCounts, notice: SummaryNotice, today: IsoDate) {
  counts.total++;
  counts[notice.kind]++;
  if (notice.filed) counts.filed++;
  else {
    counts.pending++;
    if (notice.deadline < today) counts.overdue++;
  }
}

export interface SummaryInput {
  operations: readonly SummaryOperation[];
  notices: readonly SummaryNotice[];
  thresholds: Thresholds;
  today: IsoDate;
}

/** Figures of every operation and notice given. */
export function summarizeOperations(input: SummaryInput): OperationsSummary {
  const summary = emptySummary();
  for (const op of input.operations) addOperation(summary, op, input.thresholds);
  for (const notice of input.notices) addNotice(summary.notices, notice, input.today);
  return summary;
}

/** Figures per calendar month: operations by their date, notices by their reporting month. */
export function summarizeByMonth(input: SummaryInput): Map<IsoMonth, OperationsSummary> {
  const months = new Map<IsoMonth, OperationsSummary>();
  const of = (month: IsoMonth) => {
    let summary = months.get(month);
    if (!summary) {
      summary = emptySummary();
      months.set(month, summary);
    }
    return summary;
  };
  for (const op of input.operations) addOperation(of(monthOf(op.date)), op, input.thresholds);
  for (const notice of input.notices) addNotice(of(notice.month).notices, notice, input.today);
  return months;
}

/** How an operation stands with respect to the notice and identification thresholds. */
export type OperationStatus =
  /** The operation triggers a notice (individual, or the one that completed an accumulation). */
  | { kind: "notice"; notice: NoticeObligation }
  /** Part of an accumulated notice triggered by a later operation. */
  | { kind: "covered"; notice: NoticeObligation }
  | { kind: "identification" }
  | { kind: "below_threshold" };

export function operationStatus(
  op: Pick<Operation, "id" | "amount">,
  noticeIndex: ReadonlyMap<string, readonly NoticeObligation[]>,
  thresholds: Thresholds,
): OperationStatus {
  const notices = noticeIndex.get(op.id) ?? [];
  const triggered = notices.find((n) => n.triggerOperationId === op.id);
  if (triggered) return { kind: "notice", notice: triggered };
  const covering = notices[0];
  if (covering) return { kind: "covered", notice: covering };
  return operationLevel(op.amount, thresholds) === "identification"
    ? { kind: "identification" }
    : { kind: "below_threshold" };
}
